/* eslint-disable */
import React, { useEffect, useState } from "react";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase";

const DEFAULTS = {
  enabled: true,
  allowParentDownload: true,
  releaseDay: 1,
  downloadWindowDays: 7,
  includeCalendarView: true,
  includeSimpleSummaryLanguage: true,
};

export default function MonthlyReportsSettingsCard({
  schoolId,
  adminUid,
  initialData = null,
}) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    async function loadSettings() {
      if (!schoolId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        if (initialData) {
          if (!mounted) return;
          setSettings(normaliseSettings(initialData));
          setLoading(false);
          return;
        }

        const refDoc = doc(db, "schools", schoolId, "settings", "monthlyReports");
        const snap = await getDoc(refDoc);

        if (!mounted) return;

        if (snap.exists()) {
          setSettings(normaliseSettings(snap.data()));
        } else {
          setSettings(DEFAULTS);
          await setDoc(
            refDoc,
            {
              ...DEFAULTS,
              updatedAt: serverTimestamp(),
              updatedBy: adminUid || null,
            },
            { merge: true }
          );
        }
      } catch (err) {
        console.error("Failed to load monthly report settings:", err);
        setError("Failed to load monthly report settings.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSettings();

    return () => {
      mounted = false;
    };
  }, [schoolId, adminUid, initialData]);

  function updateField(key, value) {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  async function handleSave(e) {
    if (e?.preventDefault) e.preventDefault();

    if (!schoolId) {
      setError("Missing school ID.");
      return;
    }

    try {
      setSaving(true);
      setMessage("");
      setError("");

      const payload = {
        enabled: !!settings.enabled,
        allowParentDownload: !!settings.allowParentDownload,
        releaseDay: clampNumber(settings.releaseDay, 1, 28, 1),
        downloadWindowDays: clampNumber(settings.downloadWindowDays, 1, 60, 7),
        includeCalendarView: !!settings.includeCalendarView,
        includeSimpleSummaryLanguage: !!settings.includeSimpleSummaryLanguage,

        // legacy compatibility fields
        parentDownloadEnabled: !!settings.allowParentDownload,
        includeSimpleSummary: !!settings.includeSimpleSummaryLanguage,

        updatedAt: serverTimestamp(),
        updatedBy: adminUid || null,
      };

      await setDoc(
        doc(db, "schools", schoolId, "settings", "monthlyReports"),
        payload,
        { merge: true }
      );

      setSettings(normaliseSettings(payload));
      setMessage("Monthly report settings saved.");
    } catch (err) {
      console.error("Failed to save monthly report settings:", err);
      setError("Failed to save monthly report settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section style={styles.card}>
      <div>
        <h2 style={styles.title}>Monthly Attendance Reports</h2>
        <p style={styles.subtitle}>
          Control whether parents receive simple monthly attendance PDF summaries,
          when reports are released, and how long downloads stay active.
        </p>
      </div>

      {message ? <div style={styles.success}>{message}</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}

      {loading ? (
        <div style={styles.loading}>Loading monthly report settings...</div>
      ) : (
        <>
          <ToggleRow
            title="Enable monthly reports"
            text="Turn the full monthly attendance report system on or off."
            checked={settings.enabled}
            onChange={(value) => updateField("enabled", value)}
          />

          <ToggleRow
            title="Allow parent PDF download"
            text="Parents can download the monthly PDF during the active window."
            checked={settings.allowParentDownload}
            onChange={(value) => updateField("allowParentDownload", value)}
          />

          <div style={styles.twoGrid}>
            <div style={styles.fieldCard}>
              <label style={styles.label}>Release day</label>
              <p style={styles.help}>
                Day of the month when reports become available.
              </p>
              <input
                type="number"
                min="1"
                max="28"
                value={settings.releaseDay}
                onChange={(e) => updateField("releaseDay", e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={styles.fieldCard}>
              <label style={styles.label}>Download window (days)</label>
              <p style={styles.help}>
                How long parents can download the PDF before the button disables.
              </p>
              <input
                type="number"
                min="1"
                max="60"
                value={settings.downloadWindowDays}
                onChange={(e) =>
                  updateField("downloadWindowDays", e.target.value)
                }
                style={styles.input}
              />
            </div>
          </div>

          <ToggleRow
            title="Include calendar view"
            text="Show the month grid with simple present, late, and absent colours."
            checked={settings.includeCalendarView}
            onChange={(value) => updateField("includeCalendarView", value)}
          />

          <ToggleRow
            title="Include simple summary language"
            text="Add easy-to-read attendance sentences for parents and children."
            checked={settings.includeSimpleSummaryLanguage}
            onChange={(value) =>
              updateField("includeSimpleSummaryLanguage", value)
            }
          />

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{
              ...styles.button,
              ...(saving ? styles.buttonDisabled : {}),
            }}
          >
            {saving ? "Saving..." : "Save Monthly Report Settings"}
          </button>
        </>
      )}
    </section>
  );
}

function ToggleRow({ title, text, checked, onChange }) {
  return (
    <label style={styles.toggleRow}>
      <div>
        <div style={styles.toggleTitle}>{title}</div>
        <div style={styles.toggleText}>{text}</div>
      </div>

      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        style={styles.checkbox}
      />
    </label>
  );
}

function clampNumber(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function normaliseSettings(data = {}) {
  return {
    enabled: data.enabled ?? DEFAULTS.enabled,
    allowParentDownload:
      data.allowParentDownload ??
      data.parentDownloadEnabled ??
      DEFAULTS.allowParentDownload,
    releaseDay: data.releaseDay ?? DEFAULTS.releaseDay,
    downloadWindowDays:
      data.downloadWindowDays ?? DEFAULTS.downloadWindowDays,
    includeCalendarView:
      data.includeCalendarView ?? DEFAULTS.includeCalendarView,
    includeSimpleSummaryLanguage:
      data.includeSimpleSummaryLanguage ??
      data.includeSimpleSummary ??
      DEFAULTS.includeSimpleSummaryLanguage,
  };
}

const styles = {
  card: {
    padding: 26,
    borderRadius: 24,
    background: "#fff",
    border: "1px solid rgba(23,28,58,0.08)",
    boxShadow: "0 14px 34px rgba(24,39,75,0.06)",
    display: "grid",
    gap: 16,
  },
  title: {
    margin: 0,
    fontSize: 30,
    lineHeight: 1,
    letterSpacing: "-0.04em",
    fontWeight: 900,
    color: "#101936",
  },
  subtitle: {
    margin: "12px 0 0",
    color: "#66708c",
    lineHeight: 1.65,
    fontSize: 15,
  },
  loading: {
    borderRadius: 16,
    padding: 16,
    background: "#f8fbff",
    border: "1px solid #dbe7ff",
    color: "#64748b",
    fontWeight: 800,
  },
  success: {
    padding: "12px 14px",
    borderRadius: 14,
    background: "#ecfdf3",
    color: "#166534",
    border: "1px solid #bbf7d0",
    fontWeight: 800,
  },
  error: {
    padding: "12px 14px",
    borderRadius: 14,
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
    fontWeight: 800,
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    padding: 16,
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#fbfdff",
    cursor: "pointer",
  },
  toggleTitle: {
    fontSize: 15,
    fontWeight: 900,
    color: "#101936",
  },
  toggleText: {
    marginTop: 4,
    color: "#66708c",
    fontSize: 13,
    lineHeight: 1.45,
  },
  checkbox: {
    width: 18,
    height: 18,
    flexShrink: 0,
    accentColor: "#4F46E5",
  },
  twoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },
  fieldCard: {
    padding: 16,
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#fbfdff",
  },
  label: {
    display: "block",
    fontSize: 15,
    fontWeight: 900,
    color: "#101936",
    marginBottom: 6,
  },
  help: {
    margin: "0 0 12px",
    color: "#66708c",
    fontSize: 13,
    lineHeight: 1.45,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 14,
    border: "1px solid #dbe7ff",
    padding: "13px 14px",
    background: "#fff",
    color: "#101936",
    fontWeight: 800,
    fontSize: 15,
    outline: "none",
  },
  button: {
    marginTop: 4,
    width: "fit-content",
    border: "none",
    borderRadius: 16,
    padding: "15px 18px",
    background: "linear-gradient(135deg,#4f46e5 0%,#6d5cff 100%)",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
    boxShadow: "0 16px 30px rgba(79,70,229,0.18)",
  },
  buttonDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
};